<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'

import { getCustomVars, getPointsDefaultNotificationText } from '../utils'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { ContestGameReward, MessageTemplate } from '@/types/types'

const props = defineProps<{
  reward: ContestGameReward
  messageTemplate: MessageTemplate
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:reward', reward: ContestGameReward): void
}>()

const notification = useNotification()
const { user } = useLocalStorage()
const { t } = useI18n()
const { getStoresByPermission } = useStore()
const { noSpecialCharacters } = useRegex()
const { isPolite } = useMerchantTexts()

const editingReward = ref<ContestGameReward>({
  ...props.reward
})
const mailSubject = ref(props.messageTemplate.subject)

const storesItem = ref<any[]>([])

const hasMessageContentError = ref(false)

const channelType = computed(() => {
  return props.messageTemplate.messageChannelId === 1 ? 'sms' : 'mail'
})

const invalidCharacters = computed(
  () => channelType.value === 'sms' && noSpecialCharacters(editingReward.value?.notificationTextOverride)
)

const saveReward = () => {
  if (invalidCharacters.value) {
    notification.create('error', {
      text: t('ui.error.strings.unauthorizedEmoji'),
      timeout: 2000
    })
    return
  }

  if (!editingReward.value.notificationTextOverride) {
    editingReward.value.notificationTextOverride = getPointsDefaultNotificationText(t, isPolite.value)
  }

  emit('update:reward', editingReward.value)
  emit('close')
}

const messageLength = ref(0)
const isCheckingMessageLength = ref(false)
const variablesLength = ref<Record<string, number>>({})

const hasCompatibleVoucherIntegration = computed(() =>
  Boolean((user.value?.merchant as any)?.hasCompatibleVoucherIntegration)
)

const messageMaxLength = computed(() => (channelType.value === 'sms' ? 160 : 10000))

const customVariables = computed<CustomVars[]>(() =>
  getCustomVars(editingReward.value, variablesLength.value, t, hasCompatibleVoucherIntegration.value)
)

const handleMessageLengthCalculation = () => {
  isCheckingMessageLength.value = true
  messageLength.value = null

  getMessageLength()
}

const getMessageLength = useDebounceFn(async () => {
  if (!editingReward.value.notificationTextOverride?.length) {
    messageLength.value = 0
    isCheckingMessageLength.value = false
  } else {
    try {
      const { data } = await axios.get('contest-games/message-length', {
        params: {
          message: editingReward.value.notificationTextOverride,
          rewardName: editingReward.value.name,
          points: editingReward.value.points || 0,
          type: channelType.value
        }
      })
      messageLength.value = data.data.messageLength

      isCheckingMessageLength.value = false
    } catch {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }
}, 500)

const getVariablesLength = async () => {
  try {
    const { data } = await axios.get('contest-games/variables-length', {
      params: {
        rewardName: editingReward.value.name,
        points: editingReward.value.points || 0,
        type: channelType.value
      }
    })
    variablesLength.value = data.data
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
    })
  }
}

const handleMessageContentError = (error: boolean) => {
  hasMessageContentError.value = error
}

onMounted(async () => {
  storesItem.value = getStoresByPermission('reward.read')
  await getVariablesLength()

  if (editingReward.value.notificationTextOverride) {
    handleMessageLengthCalculation()
  }
})
</script>

<template>
  <BaseModal
    :heading-title="t('pages.contests.modal.editPointReward.title')"
    is-open
    :show-footer="false"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <AppFormInput
        id="name"
        v-model="editingReward.name"
        :label="t('pages.contests.contestsCreatePage.prices.form.price.name')"
        @update:model-value="handleMessageLengthCalculation"
      />

      <AppFormInput
        id="numberOfPoint"
        v-model="editingReward.points"
        type="number"
        placeholder="0"
        :min="0"
        :label="t('pages.contests.contestsCreatePage.prices.form.price.labelPoint')"
        @update:model-value="handleMessageLengthCalculation"
      />

      <div v-if="channelType === 'mail'" class="flex flex-col gap-3">
        <label
          for="messageSubject"
          class="block cursor-not-allowed text-sm font-medium text-foreground sm:mt-px sm:pt-2"
        >
          {{ t('pages.contests.contestsCreatePage.message.form.fields.emailObject') }}
        </label>
        <AppFormInput
          id="messageSubject"
          v-model="mailSubject"
          type="text"
          :disabled="true"
          class="cursor-not-allowed opacity-50"
        />
      </div>

      <TextareaWithVars
        v-model="editingReward.notificationTextOverride"
        label="about"
        :max-length="messageMaxLength"
        :i18n-strings="{
          textareaLabel: t(
            `pages.contests.contestsCreatePage.message.form.fields.notificationTextOverride.${channelType}`
          ),
          characters: t('global.words.characters'),
          insertVar: t('global.customVars.insertLabel'),
          errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
          customValue: {
            placeholder: t('global.customVars.customValue.placeholder'),
            create: t('global.customVars.customValue.add'),
            update: t('global.customVars.customValue.modify'),
            back: t('global.customVars.customValue.back')
          }
        }"
        :custom-vars="customVariables"
        :sanitize-for-gsm="channelType === 'sms'"
        :required-store-ids="storesItem.map((store) => store.id)"
        @update:model-value="handleMessageLengthCalculation"
        @has-error="handleMessageContentError"
      >
        <template #messageLength>
          <span v-if="isCheckingMessageLength"> {{ t('global.words.loading') }}... </span>
          <span v-else>
            <span :class="{ 'text-red-600': messageLength > messageMaxLength }">
              {{ messageLength }}/{{ messageMaxLength }} {{ t('global.words.characters') }}
            </span>
          </span>
        </template>
      </TextareaWithVars>
    </div>

    <div class="mt-10 flex items-center justify-center gap-2">
      <Button variant="outline" class="flex-1" @click="emit('close')">
        {{ t('global.words.cancel') }}
      </Button>
      <Button variant="default-primary" class="flex-1" :disabled="hasMessageContentError" @click="saveReward">
        {{ t('global.words.submit') }}
      </Button>
    </div>
  </BaseModal>
</template>
